import React, { useState } from 'react';
import { adminApi } from '../services/api';
import type { Retour } from '../types';
import { libelleSousType, libelleType } from '../utils/libelles';

/**
 * S115 : « C'est juste » ou « Signaler une erreur », dans le détail d'un mail.
 * Un retour par mail ; le renvoyer le remplace. Les choix viennent de listes
 * fermées (types connus, adresses déjà vues) pour que l'export soit exploitable.
 */

/** Les couples type / sous-type du moteur de règles (RuleEngine.CLASSIFICATION_TAGS). */
export const COUPLES: [string, string][] = [
  ['client', 'quote_request'], ['client', 'quote_to_enter'], ['client', 'client_order'],
  ['client', 'client_order_confirmation'], ['client', 'document_request'], ['client', 'internal_request'], ['client', 'sales_journal'],
  ['supplier', 'supplier_offer'], ['supplier', 'supplier_coc'], ['supplier', 'supplier_fds'],
  ['supplier', 'supplier_invoice'], ['supplier', 'supplier_order_confirmation'],
  ['supplier', 'payment_reminder'], ['supplier', 'payment_advice'],
  ['web', 'web_order'], ['web', 'web_info_request'],
  ['mailchimp', 'mailchimp_no_contact'], ['mailchimp', 'mailchimp_generic_contact'],
  ['mailchimp', 'mailchimp_specific_contact'],
  ['unknown', 'to_qualify'],
];

const CHAMPS: [string, string][] = [
  ['type', 'Le type'], ['destinataire', 'Le destinataire'], ['dossier', 'Le dossier'],
  ['rien', 'Il ne fallait rien faire'], ['autre', 'Autre'],
];

interface Props {
  messageId: string;
  retour: Retour | null | undefined;
  typeAgent?: string;
  sousTypeAgent?: string;
  destinatairesAgent: string[];
  dossierAgent?: string;
  adressesConnues: string[];
  onChange: (r: Retour | null) => void;
}

const pastille = (actif: boolean): React.CSSProperties => ({
  fontSize: '0.8rem', padding: '2px 10px', borderRadius: '999px', cursor: 'pointer',
  border: `1px solid ${actif ? '#212529' : '#dee2e6'}`,
  background: actif ? '#212529' : '#fff', color: actif ? '#fff' : '#333', userSelect: 'none',
});

const bouton = (fond: string, bord: string, texte: string): React.CSSProperties => ({
  fontSize: '0.82rem', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer',
  background: fond, border: `1px solid ${bord}`, color: texte,
});

export const RetourMail: React.FC<Props> = ({
  messageId, retour, typeAgent, sousTypeAgent, destinatairesAgent, dossierAgent, adressesConnues, onChange,
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [champs, setChamps] = useState<string[]>(retour?.champs_faux || []);
  const [couple, setCouple] = useState<string>(
    retour?.type_propose && retour?.sous_type_propose ? `${retour.type_propose}/${retour.sous_type_propose}` : '');
  const [destinataire, setDestinataire] = useState(retour?.destinataire_propose || '');
  const [dossier, setDossier] = useState(retour?.dossier_propose || '');
  const [commentaire, setCommentaire] = useState(retour?.commentaire || '');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const envoyer = async (r: Retour) => {
    setEnCours(true); setErreur(null);
    try {
      onChange(await adminApi.enregistrerRetour(messageId, r));
      setOuvert(false);
    } catch (e: any) {
      setErreur(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async () => {
    setEnCours(true);
    try { await adminApi.retirerRetour(messageId); onChange(null); } finally { setEnCours(false); }
  };

  const basculer = (c: string) =>
    setChamps(cs => (cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]));

  const signaler = () => {
    const [type_propose, sous_type_propose] = couple ? couple.split('/') : [null, null];
    envoyer({
      verdict: 'erreur', champs_faux: champs,
      type_propose, sous_type_propose,
      destinataire_propose: destinataire || null, dossier_propose: dossier || null,
      commentaire: commentaire || null,
    });
  };

  return (
    <div className="mt-3">
      {retour && !ouvert && (
        <div className="small mb-2" style={{ color: retour.verdict === 'juste' ? '#146c43' : '#7a5a00' }}>
          <i className={`bi ${retour.verdict === 'juste' ? 'bi-check-circle-fill' : 'bi-flag-fill'} me-1`}></i>
          {retour.verdict === 'juste' ? 'Marqué juste.' : 'Erreur signalée'}
          {retour.verdict === 'erreur' && retour.sous_type_propose && <> : c'était {libelleType(retour.type_propose)} · {libelleSousType(retour.sous_type_propose)}</>}
          {retour.verdict === 'erreur' && retour.destinataire_propose && <>, pour {retour.destinataire_propose}</>}
          {retour.commentaire && <span className="text-muted"> « {retour.commentaire} »</span>}
          <span role="button" className="ms-2 text-decoration-underline text-muted" onClick={retirer}>retirer</span>
        </div>
      )}

      {!ouvert && (
        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
          {retour?.verdict !== 'juste' && (
            <span role="button" style={bouton('#f0faf4', '#a3cfbb', '#146c43')} aria-disabled={enCours}
                  onClick={() => !enCours && envoyer({ verdict: 'juste', champs_faux: [] })}>
              <i className="bi bi-check2 me-1"></i>C'est juste
            </span>
          )}
          <span role="button" style={bouton('#fffaf0', '#f0c36d', '#7a5a00')} onClick={() => setOuvert(true)}>
            <i className="bi bi-flag me-1"></i>{retour?.verdict === 'erreur' ? 'Modifier le signalement' : 'Signaler une erreur'}
          </span>
        </div>
      )}

      {ouvert && (
        <div className="bg-white rounded p-3" style={{ border: '1px solid #f0c36d', fontSize: '0.85rem' }}>
          <div className="mb-3">
            <div className="text-muted mb-1">Qu'est-ce qui est faux ?</div>
            <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
              {CHAMPS.map(([c, l]) => (
                <span key={c} role="button" style={pastille(champs.includes(c))} onClick={() => basculer(c)}>{l}</span>
              ))}
            </div>
          </div>

          {champs.includes('type') && (
            <div className="mb-3">
              <div className="text-muted mb-1">C'était en fait</div>
              <select className="form-select form-select-sm" style={{ maxWidth: 360 }} value={couple} onChange={e => setCouple(e.target.value)}>
                <option value="">Choisir...</option>
                {COUPLES.map(([t, s]) => (
                  <option key={`${t}/${s}`} value={`${t}/${s}`}>{libelleType(t)} · {libelleSousType(s)}</option>
                ))}
              </select>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                L'agent a dit : {libelleType(typeAgent)}{sousTypeAgent ? ` · ${libelleSousType(sousTypeAgent)}` : ''}
              </div>
            </div>
          )}

          {champs.includes('destinataire') && (
            <div className="mb-3">
              <div className="text-muted mb-1">Aurait dû aller à</div>
              <input className="form-control form-control-sm" style={{ maxWidth: 360 }} list={`adresses-${messageId}`}
                     value={destinataire} onChange={e => setDestinataire(e.target.value)} placeholder="adresse ou personne" />
              <datalist id={`adresses-${messageId}`}>
                {adressesConnues.map(a => <option key={a} value={a} />)}
              </datalist>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                L'agent l'aurait transféré à : {destinatairesAgent.length ? destinatairesAgent.join(', ') : 'personne'}
              </div>
            </div>
          )}

          {champs.includes('dossier') && (
            <div className="mb-3">
              <div className="text-muted mb-1">Aurait dû être rangé dans</div>
              <input className="form-control form-control-sm" style={{ maxWidth: 360 }} value={dossier}
                     onChange={e => setDossier(e.target.value)} placeholder="nom du dossier Outlook" />
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                L'agent l'aurait rangé dans : {dossierAgent || 'aucun dossier'}
              </div>
            </div>
          )}

          <div className="mb-3">
            <div className="text-muted mb-1">Commentaire (facultatif)</div>
            <textarea className="form-control form-control-sm" rows={2} value={commentaire}
                      onChange={e => setCommentaire(e.target.value)} placeholder="Ex : c'est une demande de prix, pas une commande ferme." />
          </div>

          {erreur && <div className="text-danger small mb-2">{erreur}</div>}
          <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
            <span role="button" style={bouton('#fff', '#dee2e6', '#333')} onClick={() => setOuvert(false)}>Annuler</span>
            <span role="button" aria-disabled={!champs.length || enCours}
                  style={{ ...bouton('#212529', '#212529', '#fff'), opacity: champs.length ? 1 : 0.4 }}
                  onClick={() => champs.length && !enCours && signaler()}>
              <i className="bi bi-check2 me-1"></i>Enregistrer le signalement
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
